import cn from "@/util/cn";
import { ComponentPropsWithoutRef, ComponentPropsWithRef } from "react";

type TextInputProps = ComponentPropsWithRef<"input">;

export const TextInput = ({ className, ...otherProps }: TextInputProps) => (
  <input
    className={cn(
      "block w-full rounded-md border-0 px-1.5 py-1.5 text-sm leading-6 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 disabled:text-gray-400",
      className
    )}
    type="text"
    {...otherProps}
  />
);

type NumberInputProps = {
  step?: number;
  isError?: boolean;
} & Omit<ComponentPropsWithRef<"input">, "type" | "step">;

export const NumberInput = ({ step, className, isError, ...otherProps }: NumberInputProps) => (
  <input
    type="number"
    inputMode="numeric"
    className={cn(
      "m-0 block min-w-0 no-spinner rounded-md border-0 p-0 leading-6 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 disabled:text-gray-400",
      isError ? "ring-red-600 focus:ring-red-600" : "",
      className
    )}
    step={step}
    {...otherProps}
  />
);

type EmailInputProps = Omit<ComponentPropsWithRef<"input">, "autoComplete"> & {
  autoComplete?: "email" | "username";
};

export const EmailInput = ({
  autoComplete = "email",
  className,
  ...otherProps
}: EmailInputProps) => (
  <input
    className={cn(
      "block w-full rounded-md border-0 px-1.5 py-1.5 text-sm leading-6 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 disabled:text-gray-400",
      className
    )}
    type="email"
    autoComplete={autoComplete}
    {...otherProps}
  />
);

type PasswordInputProps = {
  autoComplete?: "current-password" | "new-password";
} & Omit<ComponentPropsWithRef<"input">, "autoComplete">;

export const PasswordInput = ({ className, ...otherProps }: PasswordInputProps) => (
  <input
    className={cn(
      "block w-full rounded-md border-0 px-1.5 py-1.5 text-sm leading-6 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 disabled:text-gray-400",
      className
    )}
    type="password"
    {...otherProps}
  />
);

type SelectInputProps<T extends string | number> = {
  onChange: (value: T) => void;
  options: ({ value: T; title: string } & Omit<
    ComponentPropsWithoutRef<"option">,
    "key" | "value" | "children"
  >)[];
  className?: string;
} & Omit<ComponentPropsWithRef<"select">, "onChange" | "children">;

export function SelectInput<T extends string | number>({
  onChange,
  options,
  className,
  ...otherProps
}: SelectInputProps<T>) {
  return (
    <select
      className={cn(
        "block w-full rounded-md border-0 pb-1.5 pl-1.5 pr-10 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 disabled:text-gray-400",
        className
      )}
      onChange={(e) => onChange(e.target.value as T)}
      {...otherProps}
    >
      {options.map(({ value, title, ...otherProps }, i) => (
        <option key={`option-${i}`} value={value} {...otherProps}>
          {title}
        </option>
      ))}
    </select>
  );
}

type DateInputProps = ComponentPropsWithRef<"input">;

export const DateInput = ({ className, ...otherProps }: DateInputProps) => (
  <input
    className={cn(
      "block w-full rounded-md border-0 px-1.5 py-1.5 text-sm leading-6 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 disabled:text-gray-400",
      className
    )}
    type="date"
    {...otherProps}
  />
);

export const RadioInput = (props: Omit<ComponentPropsWithRef<"input">, "type">) => (
  <input type="radio" {...props} />
);

type SearchInputProps = ComponentPropsWithRef<"input">;

export const SearchInput = ({ className, ...otherProps }: SearchInputProps) => (
  <div className="relative w-full">
    <span className="absolute inset-y-0 left-0 flex items-center pl-2.5">
      <span className="material-icons text-primary/60">search</span>
    </span>
    <input
      className={cn(
        "w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 placeholder:text-sm placeholder:text-primary/60 focus:border-slate-300 focus:ring-1 focus:ring-slate-300",
        "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-primary/60",
        className
      )}
      type="text"
      {...otherProps}
    />
  </div>
);
